/**
 * Typed clients for the two backends:
 *   AUTH_BASE  — kra-photo-journal-worker (invite-code auth)
 *   TOOLS_BASE — kra-tools worker (admin API: calls, stats, threads)
 *
 * Both live on the same Cloudflare account; in dev, Vite proxies them.
 * In prod, set the VITE_*_BASE_URL env vars to the deployed URLs.
 */

import type {
  BrandContextResponse,
  CallDetailResponse,
  CallRow,
  ConfigEndpoint,
  ModelConfigResponse,
  NoiseLeftoversResponse,
  NoiseRule,
  NoiseVerdict,
  ShortcutAccessResponse,
  StatsResponse,
} from '../types.js';

const AUTH_BASE = (import.meta.env['VITE_AUTH_BASE_URL'] as string | undefined) ?? '/api';

/**
 * Resolve the kra-tools Worker base URL.
 *
 * Priority:
 *   1. VITE_TOOLS_API_BASE_URL build-time env (explicit override)
 *   2. When served from any *.pages.dev → call the worker directly
 *      (Cloudflare Pages can't internally rewrite 200 cross-origin, so the
 *      browser hits the worker URL and CORS handles it via the worker's
 *      ALLOWED_ADMIN_ORIGINS list).
 *   3. Otherwise relative — vite proxy in dev, or same-origin if hosted
 *      somewhere that proxies /api/* (e.g. a future custom domain w/ workers route).
 */
function resolveToolsBase(): string {
  const explicit = import.meta.env['VITE_TOOLS_API_BASE_URL'] as string | undefined;
  if (explicit) return explicit;
  if (typeof window !== 'undefined' && window.location.hostname.endsWith('.pages.dev')) {
    return 'https://kra-tools.duytrieu5693.workers.dev';
  }
  return '';
}

const TOOLS_BASE = resolveToolsBase();

// ── Auth (kra-photo-journal-worker) ──────────────────────────────────────

export interface ValidateInviteResponse {
  valid: boolean;
  label?: string | null;
  role?: string;
  error?: string;
}

export interface RedeemInviteResponse {
  token: string;
  name: string;
  role?: string;
  staffId?: string | null;
  error?: string;
}

export async function validateInvite(code: string): Promise<ValidateInviteResponse> {
  const res = await fetch(`${AUTH_BASE}/auth/validate-invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  return (await res.json()) as ValidateInviteResponse;
}

export async function redeemInvite(code: string, deviceLabel: string): Promise<RedeemInviteResponse> {
  const res = await fetch(`${AUTH_BASE}/auth/redeem-invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, deviceLabel }),
  });
  return (await res.json()) as RedeemInviteResponse;
}

export async function authMe(token: string): Promise<{ name: string; role?: string; staffId?: string } | null> {
  const res = await fetch(`${AUTH_BASE}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  return res.json() as Promise<{ name: string; role?: string; staffId?: string }>;
}

// ── Admin API (kra-tools worker) ──────────────────────────────────────────

function authHeaders(token: string | null): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listCalls(
  token: string | null,
  opts: { limit?: number; endpoint?: string } = {},
): Promise<{ calls: CallRow[] }> {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.endpoint) params.set('endpoint', opts.endpoint);
  const url = `${TOOLS_BASE}/api/calls${params.size > 0 ? '?' + params : ''}`;
  const res = await fetch(url, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`listCalls ${res.status}`);
  return res.json() as Promise<{ calls: CallRow[] }>;
}

export async function getCallDetail(
  token: string | null,
  callId: string,
): Promise<CallDetailResponse> {
  const res = await fetch(`${TOOLS_BASE}/api/calls/${encodeURIComponent(callId)}`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`getCallDetail ${res.status}`);
  return res.json() as Promise<CallDetailResponse>;
}

// ── OCR noise review ("Rác còn sót") ────────────────────────────────────

export async function getNoiseLeftovers(token: string | null, days: number): Promise<NoiseLeftoversResponse> {
  const res = await fetch(`${TOOLS_BASE}/api/noise/leftovers?days=${days}`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`getNoiseLeftovers ${res.status}`);
  return res.json() as Promise<NoiseLeftoversResponse>;
}

export async function getNoiseRules(token: string | null): Promise<NoiseRule[]> {
  const res = await fetch(`${TOOLS_BASE}/api/noise/rules`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`getNoiseRules ${res.status}`);
  return ((await res.json()) as { rules: NoiseRule[] }).rules;
}

export async function setNoiseRule(token: string | null, line: string, verdict: NoiseVerdict): Promise<NoiseRule> {
  const res = await fetch(`${TOOLS_BASE}/api/noise/rules`, {
    method: 'PUT',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ line, verdict }),
  });
  if (!res.ok) throw new Error(`setNoiseRule ${res.status}`);
  return ((await res.json()) as { rule: NoiseRule }).rule;
}

export async function deleteNoiseRule(token: string | null, key: string): Promise<void> {
  const res = await fetch(`${TOOLS_BASE}/api/noise/rules?key=${encodeURIComponent(key)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`deleteNoiseRule ${res.status}`);
}

// ── Shortcut lock ───────────────────────────────────────────────────────

export async function getShortcutAccess(token: string | null, days: number): Promise<ShortcutAccessResponse> {
  const res = await fetch(`${TOOLS_BASE}/api/shortcut-access?days=${days}`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`getShortcutAccess ${res.status}`);
  return res.json() as Promise<ShortcutAccessResponse>;
}

/** The switch needs a valid staff code — the admin API itself is open. */
export async function setShortcutLock(token: string | null, staffCode: string, on: boolean): Promise<void> {
  const res = await fetch(`${TOOLS_BASE}/api/shortcut-access/lock`, {
    method: 'PUT',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json', 'X-Staff-Code': staffCode },
    body: JSON.stringify({ on }),
  });
  if (res.status === 403) throw new Error('Mã không hợp lệ — dùng mã invitation còn hiệu lực của anh');
  if (!res.ok) throw new Error(`setShortcutLock ${res.status}`);
}

export async function getStats(token: string | null, days = 30): Promise<StatsResponse> {
  const res = await fetch(`${TOOLS_BASE}/api/stats?days=${days}`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`getStats ${res.status}`);
  return res.json() as Promise<StatsResponse>;
}

export async function getModelConfig(token: string | null): Promise<ModelConfigResponse> {
  const res = await fetch(`${TOOLS_BASE}/api/config/models`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`getModelConfig ${res.status}`);
  return res.json() as Promise<ModelConfigResponse>;
}

export async function setModelConfig(
  token: string | null,
  endpoint: ConfigEndpoint,
  modelId: string,
): Promise<void> {
  const res = await fetch(`${TOOLS_BASE}/api/config/models`, {
    method: 'PUT',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint, model_id: modelId }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`setModelConfig ${res.status}${detail ? `: ${detail}` : ''}`);
  }
}

export interface ReplySpeedResponse {
  priority_on: boolean;
  updated_at: number | null;
  by_tier: Array<{
    endpoint: string;
    tier: string;
    calls: number;
    avg_ms: number;
    avg_provider_ms: number | null;
    avg_cost_usd: number;
  }>;
}

export async function getReplySpeed(token: string | null): Promise<ReplySpeedResponse> {
  const res = await fetch(`${TOOLS_BASE}/api/config/reply-speed`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`getReplySpeed ${res.status}`);
  return res.json() as Promise<ReplySpeedResponse>;
}

export async function setReplyPriority(token: string | null, on: boolean): Promise<void> {
  const res = await fetch(`${TOOLS_BASE}/api/config/reply-speed`, {
    method: 'PUT',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ priority_on: on }),
  });
  if (!res.ok) throw new Error(`setReplyPriority ${res.status}`);
}

export async function getBrandContext(token: string | null): Promise<BrandContextResponse> {
  const res = await fetch(`${TOOLS_BASE}/api/config/brand-context`, {
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(`getBrandContext ${res.status}`);
  return res.json() as Promise<BrandContextResponse>;
}

export async function setBrandContext(token: string | null, content: string): Promise<void> {
  const res = await fetch(`${TOOLS_BASE}/api/config/brand-context`, {
    method: 'PUT',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(`setBrandContext ${res.status}${detail ? `: ${detail}` : ''}`);
  }
}
